import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, ArrowLeft, Check, Loader2, Calendar, AlertTriangle, Crown, Zap, Building2, Gift, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = { basic: Zap, pro: Crown, business: Building2 };
const PLAN_COLORS = {
  basic: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', btn: 'from-blue-600 to-blue-700' },
  pro: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600', btn: 'from-purple-600 to-purple-700' },
  business: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600', btn: 'from-amber-600 to-amber-700' },
};

const round2 = (n) => Math.round(n * 100) / 100;

const BillingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [acceptingDiscount, setAcceptingDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [discountMonths, setDiscountMonths] = useState(3);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponPercent, setCouponPercent] = useState(0);
  const [couponFixedPrices, setCouponFixedPrices] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes, histRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/billing/plans`),
        axios.get(`${BACKEND_URL}/api/billing/subscription`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/billing/history`, { withCredentials: true }),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data.subscription);
      setTransactions(histRes.data.transactions || []);
      setDiscountPercent(plansRes.data.discount_percent || 20);
      setDiscountMonths(plansRes.data.discount_months || 3);
    } catch (e) {
      console.error('Billing fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success') {
      const poll = async (attempts = 0) => {
        if (attempts >= 8) { toast.error('No se pudo verificar el pago.'); return; }
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/billing/checkout-status/${sessionId}`, { withCredentials: true });
          if (data.payment_status === 'paid') { toast.success('Pago exitoso!'); fetchData(); navigate('/billing', { replace: true }); return; }
          if (data.status === 'expired') { toast.error('Sesion de pago expirada.'); return; }
        } catch (e) {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSelectPlan = (planId) => {
    // Stripe was retired Feb 2026. All new subscriptions go through the
    // dedicated PayPal flow at /plans (Smart Buttons + trial).
    setCheckoutLoading(planId);
    const params = new URLSearchParams({ plan: planId });
    if (couponValid && couponCode) params.set('coupon', couponCode);
    navigate(`/plans?${params.toString()}`);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/billing/validate-coupon`, { code: couponCode });
      if (data.valid) {
        setCouponValid(true);
        setCouponPercent(data.percent);
        setCouponFixedPrices(data.fixed_prices || null);
        toast.success(`Codigo "${data.code}" aplicado: ${data.percent}% de descuento`);
      } else {
        setCouponValid(false);
        setCouponPercent(0);
        toast.error(data.message || 'Codigo invalido');
      }
    } catch {
      setCouponValid(false);
      toast.error('Error al validar el codigo');
    }
    setValidatingCoupon(false);
  };

  const handleCancelClick = () => {
    // If no discount active, show retention modal first
    if (!subscription?.discount_active) {
      setShowRetentionModal(true);
    } else {
      confirmCancel();
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    setShowRetentionModal(false);
    try {
      await axios.post(`${BACKEND_URL}/api/billing/cancel`, {}, { withCredentials: true });
      toast.success('Suscripcion cancelada.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const handleAcceptDiscount = async () => {
    setAcceptingDiscount(true);
    try {
      await axios.post(`${BACKEND_URL}/api/billing/accept-discount`, {}, { withCredentials: true });
      toast.success(`Descuento del ${discountPercent}% aplicado por ${discountMonths} meses!`);
      setShowRetentionModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aplicar descuento');
    } finally {
      setAcceptingDiscount(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';

  const getStatusBadge = (status) => {
    const styles = { active: 'bg-emerald-100 text-emerald-700', trialing: 'bg-blue-100 text-blue-700', expired: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-700' };
    const labels = { active: 'Activa', trialing: 'Prueba Gratuita', expired: 'Expirada', cancelled: 'Cancelada' };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.expired}`}>{labels[status] || status}</span>;
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Retention Modal */}
      {showRetentionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="retention-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in" data-testid="retention-modal">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-950 mb-2">Seguro que quieres cancelar?</h2>
              <p className="text-gray-600 leading-relaxed">
                Nos gustaria que te quedaras. Te ofrecemos un <span className="font-bold text-amber-600">{discountPercent}% de descuento</span> por los proximos <span className="font-bold text-amber-600">{discountMonths} meses</span> para que sigas disfrutando de ACBotHub.
              </p>
            </div>

            {subscription?.plan_info && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Tu plan {subscription.plan_info.name}</p>
                    <p className="text-lg font-bold text-gray-950">
                      <span className="line-through text-gray-400 mr-2">${subscription.plan_info.price}</span>
                      <span className="text-amber-600">${(subscription.plan_info.price * (1 - discountPercent / 100)).toFixed(2)}/mes</span>
                    </p>
                  </div>
                  <div className="bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full">-{discountPercent}%</div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button onClick={handleAcceptDiscount} disabled={acceptingDiscount} data-testid="accept-discount-btn"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl transition-all rounded-xl font-semibold py-3 text-base">
                {acceptingDiscount ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Aplicando...</> : 'Aceptar Descuento'}
              </Button>
              <Button onClick={confirmCancel} disabled={cancelling} variant="ghost" data-testid="confirm-cancel-btn"
                className="w-full text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl py-3">
                {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Cancelar de todas formas
              </Button>
            </div>
          </div>
        </div>
      )}

      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5 mr-2" />Volver al Dashboard</Button>
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-link"><MessageSquare className="w-7 h-7 text-indigo-600" /><span className="text-xl font-bold text-gray-950">ACBotHub</span></a>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold tracking-tight text-gray-950 mb-2">Facturacion</h1>
        <p className="text-gray-600 mb-8">Administra tu suscripcion y pagos</p>

        {/* Current Subscription */}
        {subscription && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-8" data-testid="current-subscription">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-950">Tu Suscripcion</h2>
              {getStatusBadge(subscription.status)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Plan Actual</p>
                <p className="text-lg font-semibold text-gray-950">{subscription.plan_info?.name || subscription.plan_id}</p>
                <p className="text-2xl font-bold text-indigo-600">${subscription.plan_info?.price || 0}/mes</p>
                {subscription.discount_active && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full" data-testid="discount-badge">
                      -{subscription.discount_percent || discountPercent}% por {subscription.discount_remaining_months} mes(es)
                    </span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Periodo Actual</p>
                <p className="text-sm text-gray-700">{formatDate(subscription.current_period_start)}</p>
                <p className="text-sm text-gray-700">hasta {formatDate(subscription.current_period_end)}</p>
              </div>
              <div className="flex items-end">
                {(subscription.status === 'active' || subscription.status === 'trialing') && (
                  <Button variant="outline" onClick={handleCancelClick} disabled={cancelling} data-testid="cancel-subscription-btn"
                    className="border-red-200 text-red-600 hover:bg-red-50">
                    Cancelar Suscripcion
                  </Button>
                )}
                {(subscription.status === 'expired' || subscription.status === 'cancelled') && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm font-medium">Bot pausado. Selecciona un plan.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Coupon Code */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-6" data-testid="coupon-section">
          <div className="flex items-center gap-2.5 mb-3">
            <Tag className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-gray-950">Tienes un codigo de descuento?</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="coupon-input"
              type="text" value={couponCode}
              onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
              placeholder="Ingresa tu codigo"
              className="flex-1 bg-gray-100 border-0 rounded-lg px-4 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:bg-white transition-all placeholder:text-gray-400 placeholder:normal-case"
            />
            <Button size="sm" onClick={validateCoupon} disabled={validatingCoupon || !couponCode.trim()}
              data-testid="apply-coupon-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium">
              {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
            </Button>
          </div>
          {couponValid === true && (
            <div className="mt-2 flex items-center gap-2 text-emerald-600 text-sm" data-testid="coupon-valid">
              <Check className="w-4 h-4" />{couponPercent}% de descuento aplicado
            </div>
          )}
          {couponValid === false && (
            <div className="mt-2 text-red-500 text-sm" data-testid="coupon-invalid">
              Codigo invalido. Verifica e intenta de nuevo.
            </div>
          )}
        </div>

        {/* Plans Grid */}
        <h2 className="text-2xl font-bold text-gray-950 mb-6">Planes Disponibles</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="plans-grid">
          {plans.map((plan) => {
            const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.basic;
            const Icon = PLAN_ICONS[plan.id] || Zap;
            const isCurrent = subscription?.plan_id === plan.id && !['expired', 'cancelled'].includes(subscription?.status);
            const showDiscount = subscription?.discount_active && isCurrent;
            const couponDiscount = couponValid && couponPercent > 0 ? (couponFixedPrices?.[plan.id] ?? round2(plan.price * (1 - couponPercent / 100))) : null;
            return (
              <div key={plan.id} className={`relative rounded-xl border-2 p-6 transition-all ${isCurrent ? `${colors.border} ${colors.bg} shadow-md` : 'border-gray-200 bg-white hover:shadow-md'}`}
                data-testid={`plan-card-${plan.id}`}>
                {plan.id === 'pro' && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">Popular</div>}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}><Icon className={`w-5 h-5 ${colors.accent}`} /></div>
                  <h3 className="text-xl font-bold text-gray-950">{plan.name}</h3>
                </div>
                {couponDiscount !== null ? (
                  <div className="mb-1">
                    <span className="text-lg text-gray-400 line-through">${plan.price}</span>
                    <span className="text-3xl font-bold text-emerald-600 ml-2">${couponDiscount}<span className="text-sm text-gray-500 font-normal">/mes</span></span>
                    <div className="mt-1.5" data-testid={`coupon-applied-${plan.id}`}>
                      <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full border border-emerald-200">
                        <Check className="w-3 h-3" />Codigo {couponCode} aplicado! Ahorra un {couponPercent}%
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-3xl font-bold text-gray-950 mb-1">${plan.price}<span className="text-sm text-gray-500 font-normal">/mes</span></p>
                )}
                {showDiscount && !couponDiscount && <p className="text-sm text-amber-600 font-medium mb-3">Con descuento: ${(plan.price * (1 - discountPercent / 100)).toFixed(2)}/mes</p>}
                {plan.id === 'business' && (
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="business-kit-badge">
                    <p className="text-xs font-semibold text-emerald-800 leading-snug">Incluye tu primer kit gratis</p>
                    <p className="text-[11px] text-emerald-700 leading-snug">$99 USD por cada kit adicional (pago único)</p>
                  </div>
                )}
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm text-gray-700"><Check className={`w-4 h-4 ${colors.accent}`} />{f}</li>)}
                </ul>
                {isCurrent ? (
                  <div className={`text-center py-2.5 rounded-lg font-medium ${colors.accent} ${colors.bg} border ${colors.border}`}>Plan Actual</div>
                ) : (
                  <Button onClick={() => handleSelectPlan(plan.id)} disabled={checkoutLoading === plan.id} data-testid={`select-plan-${plan.id}`}
                    className={`w-full bg-gradient-to-r ${colors.btn} text-white shadow-md hover:shadow-lg rounded-lg font-medium py-2.5`}>
                    {checkoutLoading === plan.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</> : subscription?.status === 'active' ? 'Cambiar a este plan' : 'Seleccionar Plan'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-sm text-gray-500 mb-10">Todos los planes incluyen 7 dias de prueba gratis</p>

        {/* Payment History */}
        {transactions.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="payment-history">
            <h2 className="text-xl font-bold text-gray-950 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-gray-400" />Historial de Pagos</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-gray-500 font-medium">Fecha</th>
                  <th className="text-left py-3 px-2 text-gray-500 font-medium">Plan</th>
                  <th className="text-left py-3 px-2 text-gray-500 font-medium">Monto</th>
                  <th className="text-left py-3 px-2 text-gray-500 font-medium">Estado</th>
                </tr></thead>
                <tbody>
                  {transactions.map((tx, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 px-2 text-gray-700">{formatDate(tx.created_at)}</td>
                      <td className="py-3 px-2 text-gray-700">{tx.metadata?.plan_name || tx.plan_id}</td>
                      <td className="py-3 px-2 text-gray-700 font-medium">
                        ${tx.amount} {tx.currency?.toUpperCase()}
                        {tx.discounted && <span className="ml-1 text-xs text-amber-600">(desc.)</span>}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${tx.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {tx.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BillingPage;
